import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  Case,
  CaseFilter,
  JiraIssue,
  JiraCommentsResponse,
  JiraComment,
  Alert,
} from '@/types/fcc'
import { useFCCApi } from '@/services/fraudCommandCenterService'
import { useFCCAlertStore } from '@/stores/fcc/fccAlertStore'
import { useFCCMerchantStore } from '@/stores/fcc/fccMerchantStore'
import { AuthState } from '@/types/authState'
import { FCCApiParams } from '@/types/fcc'
import { ApiError } from '@/util/ApiError'

export const useFCCCaseStore = defineStore('fccCase', () => {
  // State
  const cases = ref<Case[]>([])
  const loading = ref(false)
  const error = ref<ApiError | null>(null)
  const casesLoading = ref(false)
  const commentsLoading = ref(false)
  const selectedLayout = ref<'table' | 'list'>(
    (sessionStorage.getItem('fccCasesLayout') as 'table' | 'list') || 'table',
  )
  const availableLayouts = ref<('table' | 'list')[]>(['table', 'list'])
  const currentCase = ref<Case | null>(null)
  const currentJiraIssue = ref<JiraIssue | null>(null)
  const jiraComments = ref<JiraComment[] | null>(null)

  // UI state for alert action drawer (to avoid component emits)
  const actionDrawerVisible = ref(false)
  const selectedAlertsForAction = ref<Alert[]>([])

  // Request deduplication
  const fetchCasesPromise = ref<Promise<Case[]> | null>(null)
  const fetchJiraIssuePromise = ref<Promise<JiraIssue> | null>(null)
  const fetchJiraCommentsPromise = ref<Promise<JiraCommentsResponse> | null>(
    null,
  )

  const filters = ref<CaseFilter>({
    merchant: {
      selected: [],
      options: [],
    },
    alertType: {
      selected: [],
    },
    search: {
      value: '',
    },
    dateRange: {
      start: '',
      end: '',
    },
  })

  // API utilities
  const { getCases, getCase, getJiraIssue, getJiraIssueComments } = useFCCApi()

  /**
   * Event types per case: the opening alert plus every alert attached to it.
   * Both directions are indexed because an opening alert often carries no
   * CASE_ID of its own.
   */
  const alertEventTypesByCaseId = computed(() => {
    const alertStore = useFCCAlertStore()
    const byAlertId = new Map<number, string>()
    const byCaseId = new Map<number, Set<string>>()

    alertStore.alerts.forEach(alert => {
      if (!alert.EVENT_TYPE) return
      byAlertId.set(alert.ALERT_ID, alert.EVENT_TYPE)
      if (!alert.CASE_ID) return
      const types = byCaseId.get(alert.CASE_ID) ?? new Set<string>()
      types.add(alert.EVENT_TYPE)
      byCaseId.set(alert.CASE_ID, types)
    })

    cases.value.forEach(caseItem => {
      const openingType = caseItem.OPENING_ALERT_ID
        ? byAlertId.get(caseItem.OPENING_ALERT_ID)
        : undefined
      if (!openingType) return
      const types = byCaseId.get(caseItem.CASE_ID) ?? new Set<string>()
      types.add(openingType)
      byCaseId.set(caseItem.CASE_ID, types)
    })

    return byCaseId
  })

  // Getters
  const filteredCases = computed(() => {
    if (!Array.isArray(cases.value)) {
      return []
    }
    return cases.value.filter(caseItem => {
      // Merchant filter
      if (
        filters.value.merchant.selected.length > 0 &&
        !filters.value.merchant.selected.includes(caseItem.MERCHANT_ID)
      ) {
        return false
      }

      // Alert type filter — opening alert or any alert attached to the case.
      if (filters.value.alertType.selected.length > 0) {
        const caseTypes = alertEventTypesByCaseId.value.get(caseItem.CASE_ID)
        const matchesAlertType = filters.value.alertType.selected.some(
          selected => caseTypes?.has(selected),
        )
        if (!matchesAlertType) {
          return false
        }
      }

      // Search filter — ids render as #123, so strip a pasted hash.
      if (filters.value.search.value) {
        const searchTerm = filters.value.search.value
          .toLowerCase()
          .replace(/^#/, '')
        const searchableFields = [
          caseItem.CASE_ID.toString(),
          caseItem.MERCHANT_ID.toString(),
          caseItem.JIRA_ISSUE?.ISSUE_KEY || '',
        ]
        const matchesSearch = searchableFields.some(field =>
          field.toLowerCase().includes(searchTerm),
        )
        if (!matchesSearch) {
          return false
        }
      }

      // Date range filter
      if (filters.value.dateRange.start || filters.value.dateRange.end) {
        const caseDate = new Date(caseItem.CREATED_AT)
        if (filters.value.dateRange.start) {
          const startDate = new Date(filters.value.dateRange.start)
          if (caseDate < startDate) return false
        }
        if (filters.value.dateRange.end) {
          const endDate = new Date(filters.value.dateRange.end)
          if (caseDate > endDate) return false
        }
      }

      return true
    })
  })

  // Actions
  const setCases = (newCases: Case[]) => {
    cases.value = Array.isArray(newCases) ? newCases : []
  }

  const setCurrentCase = (caseItem: Case | null) => {
    currentCase.value = caseItem
  }

  const setCurrentJiraIssue = (issue: JiraIssue | null) => {
    currentJiraIssue.value = issue
  }

  const setJiraComments = (comments: JiraComment[] | null) => {
    jiraComments.value = comments
  }

  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading
  }

  const openActionDrawer = (alerts: Alert[]) => {
    selectedAlertsForAction.value = alerts
    actionDrawerVisible.value = true
  }

  const closeActionDrawer = () => {
    actionDrawerVisible.value = false
    selectedAlertsForAction.value = []
  }

  /**
   * Opens the action drawer for the opening alert of the provided case id
   */
  const openActionDrawerFromCaseId = (caseId: string) => {
    const c =
      currentCase.value?.CASE_ID?.toString() === caseId
        ? currentCase.value
        : cases.value.find(cs => cs.CASE_ID.toString() === caseId) || null
    if (!c || !c.OPENING_ALERT_ID) return

    const alertStore = useFCCAlertStore()
    const merchantStore = useFCCMerchantStore()

    const opening = alertStore.alerts.find(a => a.ALERT_ID === c.OPENING_ALERT_ID)
    if (!opening) return

    const merchant = merchantStore.getMerchantById(opening.MERCHANT_ID)
    const alert: Alert = {
      ALERT_ID: opening.ALERT_ID,
      EVENT_TYPE: opening.EVENT_TYPE,
      MERCHANT_ID: opening.MERCHANT_ID,
      TIMESTAMP: opening.TIMESTAMP,
      STATISTIC: opening.STATISTIC ?? 0,
      THRESHOLD: opening.THRESHOLD ?? 0,
      SIGNIFICANCE: opening.SIGNIFICANCE ?? 0,
      TOLERANCE: opening.TOLERANCE,
      CASE_ID: c.CASE_ID,
      ACTIONED: opening.ACTIONED,
      MERCHANT_NAME: merchant?.MERCHANT_NAME,
    }

    openActionDrawer([alert])
  }

  const setSelectedLayout = (layout: 'table' | 'list') => {
    selectedLayout.value = layout
    sessionStorage.setItem('fccCasesLayout', layout)
  }

  const updateFilters = (newFilters: Partial<CaseFilter>) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const resetFilters = () => {
    filters.value = {
      merchant: {
        selected: [],
        options: filters.value.merchant.options,
      },
      alertType: {
        selected: [],
      },
      search: {
        value: '',
      },
      dateRange: {
        start: '',
        end: '',
      },
    }
  }

  const fetchCases = async (
    authState: AuthState,
    params?: FCCApiParams,
    forceRefresh = false,
  ) => {
    // If there's already a request in progress and we're not forcing a refresh, return the existing promise
    if (fetchCasesPromise.value && !forceRefresh) {
      return fetchCasesPromise.value
    }

    // Create a new promise for the request
    fetchCasesPromise.value = (async () => {
      casesLoading.value = true
      error.value = null
      try {
        const fetchedCases = await getCases(authState, params)
        cases.value = fetchedCases
        return fetchedCases
      } catch (err) {
        error.value = ApiError.isApiError(err) ? err : null
        cases.value = []
        throw err
      } finally {
        casesLoading.value = false
        // Clear the promise reference when done
        fetchCasesPromise.value = null
      }
    })()

    return fetchCasesPromise.value
  }

  const fetchCase = async (authState: AuthState, caseId: string) => {
    loading.value = true
    try {
      const caseData = await getCase(authState, caseId)
      currentCase.value = caseData
      // Set the JIRA issue from the case data directly
      if (caseData.JIRA_ISSUE) {
        currentJiraIssue.value = {
          ...caseData.JIRA_ISSUE,
          DESCRIPTION: caseData.JIRA_ISSUE.DESCRIPTION ?? '',
        }
      }
      return caseData
    } catch (error) {
      console.error('Error fetching case:', error)
      currentCase.value = null
      throw error
    } finally {
      loading.value = false
    }
  }

  const fetchJiraIssue = async (authState: AuthState, issueId: string) => {
    // If there's already a request in progress for this issue, return the existing promise
    if (fetchJiraIssuePromise.value) {
      return fetchJiraIssuePromise.value
    }

    // Create a new promise for the request
    fetchJiraIssuePromise.value = (async () => {
      loading.value = true
      try {
        const issue = await getJiraIssue(authState, issueId)
        currentJiraIssue.value = issue
        return issue
      } catch (error) {
        console.error('Error fetching JIRA issue:', error)
        currentJiraIssue.value = null
        throw error
      } finally {
        loading.value = false
        // Clear the promise reference when done
        fetchJiraIssuePromise.value = null
      }
    })()

    return fetchJiraIssuePromise.value
  }

  const fetchJiraIssueComments = async (
    authState: AuthState,
    issueId: string,
  ) => {
    // If there's already a request in progress for this issue's comments, return the existing promise
    if (fetchJiraCommentsPromise.value) {
      return fetchJiraCommentsPromise.value
    }

    // Create a new promise for the request
    fetchJiraCommentsPromise.value = (async () => {
      commentsLoading.value = true
      try {
        const comments = await getJiraIssueComments(authState, issueId)
        jiraComments.value = comments
        return comments
      } catch (error) {
        console.error('Error fetching JIRA issue comments:', error)
        jiraComments.value = null
        throw error
      } finally {
        commentsLoading.value = false
        // Clear the promise reference when done
        fetchJiraCommentsPromise.value = null
      }
    })()

    return fetchJiraCommentsPromise.value
  }

  const clearCases = () => {
    cases.value = []
    currentCase.value = null
    currentJiraIssue.value = null
    jiraComments.value = null
    closeActionDrawer()
    // Clear promise references
    fetchCasesPromise.value = null
    fetchJiraIssuePromise.value = null
    fetchJiraCommentsPromise.value = null
  }

  const refreshCases = async (authState: AuthState, params?: FCCApiParams) => {
    return fetchCases(authState, params, true)
  }

  return {
    // State
    cases,
    loading,
    error,
    casesLoading,
    commentsLoading,
    selectedLayout,
    availableLayouts,
    currentCase,
    currentJiraIssue,
    jiraComments,
    actionDrawerVisible,
    selectedAlertsForAction,
    filters,

    // Getters
    filteredCases,

    // Actions
    setCases,
    setCurrentCase,
    setCurrentJiraIssue,
    setJiraComments,
    setLoading,
    openActionDrawer,
    closeActionDrawer,
    openActionDrawerFromCaseId,
    setSelectedLayout,
    updateFilters,
    resetFilters,
    fetchCases,
    fetchCase,
    fetchJiraIssue,
    fetchJiraIssueComments,
    clearCases,
    refreshCases,
  }
})
